/**
 * API 服务层
 * 封装 axios 请求，自动携带 token，统一处理错误
 * 生产环境 API 地址通过 REACT_APP_API_URL 环境变量配置
 */
import axios from 'axios';
import { message } from 'antd';

// 根据环境自动切换 API 地址
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? '/api'
    : '/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// 请求拦截器 - 自动添加 token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截器 - 统一错误处理
api.interceptors.response.use(
  response => {
    const { data } = response;
    // code=0 兼容大屏 boardscreen 接口的历史约定（其余接口均为 200）
    if (data.code !== 200 && data.code !== 0) {
      message.error(data.message || '请求失败');
      if (data.code === 401) {
        const isLoginPage = window.location.pathname.includes('/login');
        if (!isLoginPage) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_info');
          window.location.href = (process.env.PUBLIC_URL || '') + '/login';
        }
      }
      return Promise.reject(data);
    }
    return data;
  },
  error => {
    if (error.response?.status === 401) {
      const isLoginPage = window.location.pathname.includes('/login');
      if (!isLoginPage) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_info');
        window.location.href = (process.env.PUBLIC_URL || '') + '/login';
      }
    }
    message.error(error.response?.data?.message || error.message || '网络错误');
    return Promise.reject(error);
  }
);

// ==================== 认证 ====================
export const authApi = {
  login: (username, password) => api.post('/auth/admin-login', { username, password }),
  register: (data) => api.post('/auth/register', data),
  getInfo: () => api.get('/auth/admin-info'),
};

// ==================== 维修工单 ====================
export const repairApi = {
  list: (params) => api.get('/repair/list', { params }),
  detail: (id) => api.get(`/repair/detail/${id}`),
  create: (data) => api.post('/repair/admin-create', data),
  receive: (id, data) => api.post(`/repair/${id}/receive`, data),
  quote: (id, data) => api.post(`/repair/${id}/quote`, data),
  updateStatus: (id, data) => api.put(`/repair/${id}/status`, data),
  ship: (id, data) => api.post(`/repair/${id}/ship`, data),
  // 客户在管理后台的操作
  adminPay: (id) => api.post(`/repair/${id}/admin-pay`),
  adminAbandon: (id, reason) => api.post(`/repair/${id}/admin-abandon`, { reason }),
  // 线下收款登记（上门收机/门店自取等线下场景）
  offlinePay: (id, data) => api.post(`/repair/${id}/offline-pay`, data),
  // 后台退款（通联原路退回，仅管理员；订单取消后退回剩余金额）
  refund: (id, reason) => api.post('/payment/refund', { orderType: 'repair', orderId: id, reason }),
};

// ==================== 销售 ====================
export const salesApi = {
  list: (params) => api.get('/sales/list', { params }),
  detail: (id) => api.get(`/sales/detail/${id}`),
  create: (data) => api.post('/sales/create', data),
  pay: (id, data) => api.put(`/sales/${id}/pay`, data),
  ship: (id, data) => api.post(`/sales/${id}/ship`, data),
};

// ==================== 返修 ====================
export const returnApi = {
  list: (params) => api.get('/return/list', { params }),
  detail: (id) => api.get(`/return/detail/${id}`),
  create: (data) => api.post('/return/admin-create', data),
  updateStatus: (id, data) => api.put(`/return/${id}/status`, data),
  complete: (id, data) => api.put(`/return/${id}/complete`, data),
};

// ==================== 配件 ====================
export const partsApi = {
  list: (params) => api.get('/parts/list', { params }),
  create: (data) => api.post('/parts/create', data),
  update: (id, data) => api.put(`/parts/${id}`, data),
  remove: (id) => api.delete(`/parts/${id}`),
  stockAlert: () => api.get('/parts/stock-alert'),
};

// ==================== 统一支付（微信+支付宝二合一） ====================
export const paymentApi = {
  // 统一下单
  unified: (data) => api.post('/payment/unified', data),
  // 查询支付状态
  status: (orderType, orderId) => api.get(`/payment/status/${orderType}/${orderId}`),
};

// ==================== 大屏数据 ====================
export const boardscreenApi = {
  getData: () => api.get('/boardscreen/data'),
};

// ==================== 员工管理 ====================
export const employeeApi = {
  list: () => api.get('/employees/list'),
  create: (data) => api.post('/employees/create', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  resetPassword: (id, newPassword) => api.put(`/employees/${id}/reset-password`, { newPassword }),
  updateStatus: (id, status) => api.put(`/employees/${id}/status`, { status }),
  remove: (id) => api.delete(`/employees/${id}`),
  convertToCustomer: (id) => api.post(`/employees/${id}/convert-customer`),
  changePassword: (data) => api.put('/employees/change-password', data),
  roles: () => api.get('/employees/roles'),
};

// ==================== 客户管理 ====================
export const customerApi = {
  list: (params) => api.get('/customers/list', { params }),
  create: (data) => api.post('/customers/create', data),
  detail: (id) => api.get(`/customers/${id}`),
  update: (id, data) => api.put(`/customers/${id}`, data),
  setVip: (id, vipLevelId) => api.put(`/customers/${id}/vip`, { vipLevelId }),
  bind: (id, data) => api.put(`/customers/${id}/bind`, data),
  resetPassword: (id, password) => api.put(`/customers/${id}/reset-password`, { password }),
  remove: (id) => api.delete(`/customers/${id}`),
  merge: (primaryId, secondaryId) => api.post('/customers/merge', { primaryId, secondaryId }),
};

// ==================== VIP等级管理 ====================
export const vipLevelApi = {
  list: () => api.get('/vip-levels/list'),
  create: (data) => api.post('/vip-levels/create', data),
  update: (id, data) => api.put(`/vip-levels/${id}`, data),
  remove: (id) => api.delete(`/vip-levels/${id}`),
};

// ==================== 通用 ====================
export const commonApi = {
  health: () => api.get('/health'),
  // 图片URL转换（NAS路径或本地路径）
  getImageUrl: (path) => {
    if (!path) return '';
    // 已经是完整 URL 直接返回
    if (path.startsWith('http')) {
      return path;
    }
    // 本地路径拼接 API 地址
    const baseUrl = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace('/api', '')
      : window.location.origin;
    return `${baseUrl}/${path}`;
  },
};

export default api;
